/**
 * @module Toast
 * @description This module provides a toast component for notifications.
 * Checkout https://fkhadra.github.io/react-toastify/category/guides for more details.
 */
import { FC } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Alert, styled, Typography, useTheme } from '@mui/material';
import ToastErrorIcon from '../assets/toast_error_icon.svg';
import ToastSuccessIcon from '../assets/toast_success_icon.svg';

// Update default react-toastify styles, make it consistent with the MUI Alert component.
export const StyledToastContainer = styled(ToastContainer)(({ theme }) => ({
  top: '15px',
  right: '15px',
  '.Toastify__toast': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[300]
        : theme.palette.common.white,
    boxShadow: '0 14px 64px 0 var(--Drop-Shadow, rgba(51, 51, 51, 0.14))',
    padding: '10px',
    margin: '0',
    minHeight: '75px',
    maxHeight: '120px',
    width: '420px',
    borderRadius: '0',
    position: 'relative',
    overflow: 'hidden',
  },
  '.Toastify__toast--success': {
    borderLeft: `6px solid ${theme.palette.success.main}`,
  },
  '.Toastify__toast--error': {
    borderLeft: `6px solid ${theme.palette.error.main}`,
  },
  '.Toastify__toast-body': {
    padding: '0',
    margin: '0',
    overflow: 'hidden',
  },
  '.Toastify__close-button': {
    color: theme.palette.text.secondary,
    fontSize: '18px',
    width: '20px',
    height: '20px',
    lineHeight: '1',
    position: 'absolute',
    top: '50%',
    right: '12px',
    transform: 'translateY(-50%)',
    opacity: 1,
    '&:hover, &:focus': {
      color: theme.palette.text.primary,
      opacity: 1,
    },
  },
  '.Toastify__close-button svg': {
    width: '18px',
    height: '18px',
    fill: 'currentColor',
  },
}));

const CustomToast: FC<{
  message: string;
  severity: 'success' | 'error';
}> = ({ message, severity }) => {
  const theme = useTheme();

  return (
    <Alert
      severity={severity}
      icon={
        <img
          src={severity === 'success' ? ToastSuccessIcon : ToastErrorIcon}
          alt={severity === 'success' ? 'Success' : 'Error'}
          width={38}
          height={38}
        />
      }
      sx={{
        overflow: 'hidden',
        height: '75px',
        width: '380px',
        display: 'flex',
        alignItems: 'center',
        '& .MuiAlert-message': {
          overflow: 'hidden',
          maxLines: 2,
        },
      }}
    >
      {severity === 'success' && (
        <Typography
          variant='subtitle2'
          color={theme.palette.success.main}
          sx={{
            fontWeight: 600,
          }}
        >
          Success
        </Typography>
      )}
      {severity === 'error' && (
        <Typography
          variant='subtitle2'
          color={theme.palette.error.main}
          sx={{
            fontWeight: 600,
          }}
        >
          Error
        </Typography>
      )}
      {message}
    </Alert>
  );
};

export const notifySuccess = (message: string) =>
  toast.success(<CustomToast message={message} severity='success' />, {
    icon: false,
  });

export const notifyError = (message: string) =>
  toast.error(<CustomToast message={message} severity='error' />, {
    icon: false,
  });
